import { Modal } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import Button from 'components/button/Button';
import FormWithProvider from 'components/forms/form/Form';
import SelectField from 'components/forms/selectField/SelectField';
import TextAreaField from 'components/forms/textAreaField/TextAreaField';
import TextField from 'components/forms/textField/TextField';
import useAppModal from 'hooks/useAppModal';
import useGetCurrencySymbol from 'hooks/useGetCurrencySymbol';
import { AvailableAmenity } from 'models/Amenity';
import { Currency } from 'models/Currency';
import { AvailableRule } from 'models/Rule';
import {
  AmenityEditModalFooter,
  AmenityTypeName,
} from './AmenityDescription.styles';
import { EditModalSchemaType, editSchema } from './AmenityEditModal.schema';

export const amenityEditModalId = 'amenity-edit-modal';

const AmenityEditModal = ({
  amenity,
  currency,
  defaultValues,
  updateAmenity,
}: {
  amenity: AvailableAmenity | AvailableRule;
  currency: Currency;
  defaultValues: EditModalSchemaType;
  updateAmenity: (values: EditModalSchemaType) => void;
}) => {
  const { t } = useTranslation();
  const { closeModal } = useAppModal();
  const getCurrencySymbol = useGetCurrencySymbol();
  const type = 'amenityType' in amenity ? amenity.amenityType : amenity.rule;

  const supportedChargeFrequencies =
    'supportedChargeFrequencies' in amenity
      ? amenity.supportedChargeFrequencies
      : [];

  const chargeFrequencyOptions = [
    {
      label: t('common.pleaseSelect'),
      value: '',
    },
    ...supportedChargeFrequencies.map((value) => ({
      label: t(`amenities.chargeFrequency.${value}`),
      value,
    })),
  ];

  const handleClose = () => {
    closeModal(amenityEditModalId);
  };

  const handleSubmit = (values: EditModalSchemaType) => {
    updateAmenity(values);
  };

  return (
    <FormWithProvider
      horizontal
      resolver={editSchema()}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <Trans
            i18nKey="pageProperty.amenities.amenityWithType"
            values={{
              amenityType: t(`amenities.types.${type}`),
            }}
            components={{ 1: <AmenityTypeName /> }}
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {amenity.supportsDescription && (
          <TextAreaField
            name="description"
            label={t('pageProperty.amenities.description')}
            maxCharacters={1000}
            maxLength={1000}
            colSmInput={8}
            colSmLabel={4}
          />
        )}
        {'supportsPrice' in amenity && !!amenity.supportsPrice && (
          <TextField
            colSmInput={8}
            colSmLabel={4}
            label={t('pageProperty.amenities.price')}
            leftAddon={getCurrencySymbol(currency)}
            min={0}
            name="price"
            rightTooltip={t('pageProperty.amenities.priceTooltip')}
            step="any"
            type="number"
          />
        )}
        {supportedChargeFrequencies?.length > 1 && (
          <SelectField
            colSmInput={8}
            colSmLabel={4}
            label={t('pageProperty.amenities.frequency')}
            name="chargeFrequency"
            options={chargeFrequencyOptions}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <AmenityEditModalFooter>
          <Button bsStyle="default" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button bsStyle="primary" type="submit">
            {t('common.apply')}
          </Button>
        </AmenityEditModalFooter>
      </Modal.Footer>
    </FormWithProvider>
  );
};

export default AmenityEditModal;
